import React from "react";
import { Box, Typography, Paper, ButtonBase } from "@mui/material";

/** POS filter field in AllItems — blur before tap so first tap registers on tablets. */
function blurPosQueryIfFocused() {
  const ae = document.activeElement;
  if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA") && ae.id === "query") {
    ae.blur();
  }
}

/** Full-width option tile inside 4-column Grid layouts (POS catalog, variant pickers). */
export const OPTION_GRID_TILE_SX = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  boxSizing: "border-box",
  flex: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "75px",
  borderRadius: "4px",
  cursor: "pointer",
  overflow: "hidden",
};

const defaultPaperSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "stretch",
  width: "140px",
  minHeight: "75px",
  backgroundColor: "#DEDEDE",
  borderRadius: "4px",
  cursor: "pointer",
  border: "none",
  color: "#000",
};

export default function Option(props) {
  const {
    price,
    img,
    title,
    titleIcon,
    description,
    selected,
    itemCount,
    "aria-label": ariaLabel,
  } = props;

  // Base + selected colors first, then `props.sx` (layout only from callers like AllItems)
  // so passing `sx` does not drop the default gray tile / burgundy selected styling.
  const paperSx = {
    position: "relative",
    ...defaultPaperSx,
    backgroundColor: selected ? "#DDD" : defaultPaperSx.backgroundColor,
    border: selected ? "2px solid #78374a" : "none",
    color: selected ? "#78374a" : "#000",
    ...(props.sx || {}),
  };

  const showCount = itemCount != null && itemCount !== "";

  return (
    <ButtonBase
      component="button"
      type="button"
      disableRipple
      aria-label={ariaLabel || title}
      onPointerDown={blurPosQueryIfFocused}
      onClick={() => props.onClick(!selected)}
      sx={{
        width: "100%",
        height: "100%",
        alignSelf: "stretch",
        color: "inherit",
        textAlign: "inherit",
        border: 0,
        padding: 0,
        margin: 0,
        background: "transparent",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderRadius: paperSx.borderRadius ?? "4px",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <Paper sx={{ ...paperSx, overflow: "hidden", maxWidth: "100%" }} elevation={selected ? 1 : 2}>
        {showCount && (
          <Typography
            component="span"
            variant="caption"
            sx={{
              position: "absolute",
              top: 6,
              right: 8,
              fontSize: "0.7rem",
              fontWeight: 600,
              lineHeight: 1,
              opacity: 0.8,
              color: "#999",
            }}
          >
            {itemCount}
          </Typography>
        )}
        <Box
          py={2}
          px={1}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {img ? (
            <Box textAlign="center">
              <img src={img} alt="" style={{ height: "24px", width: "24px" }} />
              <Typography
                sx={{ textAlign: "center", textTransform: "uppercase", fontSize: "16px" }}
              >
                {title}
              </Typography>
            </Box>
          ) : (
            <Box
              textAlign="center"
              sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {titleIcon ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      flexWrap: "nowrap",
                      width: "max-content",
                      maxWidth: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ display: "flex", color: "#888", flexShrink: 0 }} aria-hidden>
                      {titleIcon}
                    </Box>
                    <Typography
                      variant="body1"
                      noWrap
                      sx={{
                        textAlign: "center",
                        textTransform: "uppercase",
                        fontSize: "16px",
                        flex: "1 1 0",
                        minWidth: 0,
                      }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      textAlign: "center",
                      textTransform: "uppercase",
                      fontSize: "16px",
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              )}
              {description ? (
                <Typography variant="caption" sx={{ textAlign: "center", display: "block" }}>
                  {description}
                </Typography>
              ) : null}
              {price && (
                <Box textAlign={"center"}>
                  <Typography variant="body2" color="textSecondary">
                    {price}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </ButtonBase>
  );
}
